import React from "react";

import { Svg, TooltipIcon, legacyTooltipAttrs } from "../svg";

/**
 * Native port of ``cms/blocks/hc-block/hc-block--compare-adapt.html`` (#33404).
 *
 * A class-driven structural compare table; the imagery is CSS background-image
 * keyed off element classes (assets in ``img/hardcoded/`` + SCSS), so this is a
 * faithful transcription of the table DOM + class names.
 *
 * The legacy ``__react_component_tooltip`` / ``data-tip`` / ``data-for`` /
 * ``data-tooltip`` static markup is reproduced VERBATIM (a global JS tooltip lib
 * rebinds to it, so it must be present in the SSR output). The ``id="tooltip-0"``
 * collision across every tooltip is intentional legacy markup, preserved as-is.
 * The legacy ``{% include 'cms/components/svg.html' with src='tooltip-icon' ...%}``
 * becomes the shared ``Svg``/``TooltipIcon`` inline component.
 */

// The legacy tooltip pattern shared across this table: a trigger span wrapping
// the tooltip-icon svg, followed by the hidden ``__react_component_tooltip`` div.
// ``data-tooltip``/``title`` on the OUTER ``<span>`` are supplied by callers, so
// this helper only emits the trigger + content portion (the inner two spans).
const Tooltip = (props: { text: string }): React.JSX.Element => (
    <>
        <span data-tip="true" data-for="tooltip-0" {...legacyTooltipAttrs}>
            <Svg
                Icon={TooltipIcon}
                src="tooltip-icon"
                className="tooltip__handle"
            />
        </span>
        <div
            className="__react_component_tooltip place-top type-dark tooltip__content"
            id="tooltip-0"
            role="tooltip"
            data-id="tooltip"
            style={{ maxWidth: "275px" }}
        >
            {props.text}
        </div>
    </>
);

export const CompareAdapt = (): React.JSX.Element => (
    <div className="l-capped-width">
        <header>
            <h2>
                Compare TEMPUR-Adapt<sup>®</sup>
            </h2>
            <p>
                Superior pressure relief and motion cancellation for deeper,
                more rejuvenating sleep
            </p>
        </header>
        <div>
            <table>
                <thead>
                    <tr>
                        <th>
                            Mattress
                            <br />
                            Models
                        </th>
                        <th>
                            <div>
                                TEMPUR<sup>®</sup>
                            </div>
                            Technology
                        </th>
                        <th>
                            <span>
                                Pressure
                                <br />
                                Relief{" "}
                                <span
                                    data-tooltip="Unlike most mattresses, TEMPUR® materials provide unparalleled pressure point relief and absorption."
                                    title="Unlike most mattresses, TEMPUR® materials provide unparalleled pressure point relief and absorption."
                                >
                                    <Tooltip text="Unlike most mattresses, TEMPUR® materials provide unparalleled pressure point relief and absorption." />
                                </span>
                            </span>
                        </th>
                        <th>
                            <span>
                                Profile
                                <br />
                                Height{" "}
                                <span
                                    data-tooltip="A taller profile gives you more one-of-a-kind TEMPUR® technology for additional pressure relief and conforming support."
                                    title="A taller profile gives you more one-of-a-kind TEMPUR® technology for additional pressure relief and conforming support."
                                >
                                    <Tooltip text="A taller profile gives you more one-of-a-kind TEMPUR® technology for additional pressure relief and conforming support." />
                                </span>
                            </span>
                        </th>
                        <th>
                            <span>
                                Motion
                                <div>
                                    Cancellation{" "}
                                    <span
                                        data-tooltip="Tempur-Pedic mattresses offer unmatched motion cancellation to help you and your partner sleep undisturbed longer."
                                        title="Tempur-Pedic mattresses offer unmatched motion cancellation to help you and your partner sleep undisturbed longer."
                                    >
                                        <Tooltip text="Tempur-Pedic mattresses offer unmatched motion cancellation to help you and your partner sleep undisturbed longer." />
                                    </span>
                                </div>
                            </span>
                        </th>
                        <th>
                            Available
                            <br />
                            Feels
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <span>
                                Adapt<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <span>
                                Original
                                <br />
                                Material{" "}
                                <span
                                    data-tooltip="The legendary formulation first developed by NASA and then perfected by us for sleep."
                                    title="The legendary formulation first developed by NASA and then perfected by us for sleep."
                                >
                                    <Tooltip text="The legendary formulation first developed by NASA and then perfected by us for sleep." />
                                </span>
                            </span>
                        </td>
                        <td>
                            <b>Legendary</b>
                        </td>
                        <td className="profile-height">
                            <strong>
                                11<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>Unmatched Motion Cancellation</td>
                        <td>
                            <span>
                                Med. Hybrid{" "}
                                <span
                                    data-tooltip="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed."
                                    title="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed."
                                >
                                    <Tooltip text="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed." />
                                </span>
                            </span>
                            <div>Medium</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span>
                                ProAdapt<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <span>
                                <span>
                                    APR<sup>™</sup>
                                </span>
                                <br />
                                Material{" "}
                                <span
                                    data-tooltip="Our most pressure-relieving material ever for advanced support, comfort and relaxation."
                                    title="Our most pressure-relieving material ever for advanced support, comfort and relaxation."
                                >
                                    <Tooltip text="Our most pressure-relieving material ever for advanced support, comfort and relaxation." />
                                </span>
                            </span>
                        </td>
                        <td>
                            <div>
                                <strong>Advanced</strong>
                            </div>
                            20% More{" "}
                            <span>
                                TEMPUR<sup>®</sup>
                            </span>
                        </td>
                        <td className="profile-height">
                            <strong>
                                12<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>Unmatched Motion Cancellation</td>
                        <td>
                            Soft
                            <div>
                                Med. Hybrid{" "}
                                <span
                                    data-tooltip="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed."
                                    title="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed."
                                >
                                    <Tooltip text="TEMPUR® materials work in combination with 1,000+ premium spring coils to provide added edge support and easier movement in the bed." />
                                </span>
                            </div>
                            Medium<div>Firm</div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span>
                                LuxeAdapt<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            <span>
                                <span>
                                    APR+<sup>™</sup>
                                </span>
                                <br />
                                Material{" "}
                                <span
                                    data-tooltip="All the pressure relief of APR material, plus 30% more conforming comfort and support."
                                    title="All the pressure relief of APR material, plus 30% more conforming comfort and support."
                                >
                                    <Tooltip text="All the pressure relief of APR material, plus 30% more conforming comfort and support." />
                                </span>
                            </span>
                        </td>
                        <td>
                            <div>
                                <strong>Ultimate</strong>
                            </div>
                            40% More{" "}
                            <span>
                                TEMPUR<sup>®</sup>
                            </span>
                        </td>
                        <td className="profile-height">
                            <strong>
                                13<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>Unmatched Motion Cancellation</td>
                        <td>
                            Soft
                            <br />
                            Firm
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
